/**
 * Der API-Client der Web-App.
 *
 * Zwei Token, zwei Wege (ADR 0015):
 *
 * - Der **Zugriffstoken** liegt im Speicher (`token-store.ts`) und wird als
 *   `Authorization`-Kopf angehängt. In `localStorage` läge er für jedes
 *   eingeschleuste Skript offen.
 * - Der **Auffrischungstoken** liegt im `HttpOnly`-Cookie und wird vom Browser
 *   selbst mitgeschickt — dafür `includeCredentials`. Dazu der lesbare
 *   CSRF-Wert als `X-CSRF-Token`.
 */
import { createHausMeterClient } from "@hausmeter/sdk";
import { csrfToken } from "./csrf.js";
import { accountId, writeSession } from "./token-store.js";

// `import.meta.env` gibt es nur unter Vite. Der optionale Zugriff hält das Modul
// auch außerhalb ladbar — sonst scheitert schon der Import in jedem Test, der
// keinen Vite-Kontext hat.
const baseUrl = import.meta.env?.VITE_API_BASE_URL ?? "http://localhost:3000/api/v1";

/** Wird von der Sitzungsverwaltung gesetzt; hier nur aufgerufen. */
let onSessionLost: () => void = () => {};

export function whenSessionLost(handler: () => void): void {
  onSessionLost = handler;
}

/** Liefert den Token für eine Anfrage; die Sitzungsverwaltung setzt ihn (HM-098). */
let tokenProvider: (request: Request) => string | null | Promise<string | null> = () => null;

export function provideAccessToken(provider: typeof tokenProvider): void {
  tokenProvider = provider;
}

export const api = createHausMeterClient({
  baseUrl,
  /**
   * Der Token wird **vor** der Anfrage geprüft und notfalls aufgefrischt
   * (HM-098) — gesetzt von der Sitzungsverwaltung, weil nur sie auffrischen
   * kann; der Client kennt sie nicht (dieselbe Umkehrung wie bei
   * `whenSessionLost`).
   */
  getAccessToken: (request) => tokenProvider(request),
  getAccountId: () => accountId(),
  /**
   * Ohne dies schickt der Browser `hm_refresh` nicht mit: In der Entwicklung
   * liegt die Web-App auf Port 5173 und die API auf 3000, und das ist für
   * `fetch` ein fremder Ursprung — auch wenn es dieselbe Site ist.
   */
  includeCredentials: true,
  getCsrfToken: () => csrfToken(),
  onUnauthorized: () => {
    /**
     * Die Sitzung endet, die Anfrage wird **nicht** wiederholt. Automatisches
     * Auffrischen läuft vorausschauend in `session.ts`, bevor der Token abläuft;
     * ein `401` bedeutet hier also etwas anderes — widerrufener Token, erkannter
     * Diebstahl, gelöschtes Konto. In all diesen Fällen ist ein zweiter Versuch
     * falsch: Er würde den Widerruf umgehen wollen.
     */
    writeSession(null);
    onSessionLost();
  },
});

export { baseUrl as apiBaseUrl };
